import { computed, ref } from 'vue'
import { scrambleName } from '../demoMode/nameScrambler'

// Module-level (singleton) state — shared by every importer, including the
// fetchInterceptor which runs outside any component's setup() and needs the
// same live refs, not a fresh copy per call.
const active = ref(false)
const viewAsTeacher = ref(null) // { id, name } | null
const scrambleNames = ref(false)
const previewMissingAttendance = ref(false)
// Set by the "d" shortcut (AppRoot.vue), consumed by DemoModePanel. A flag
// rather than an event because the panel only mounts once the user menu
// opens, i.e. after the shortcut has already fired.
const previewPickerRequested = ref(false)

// real string -> fake string, kept for the life of the demo session so the
// same person maps to the same fake name everywhere it appears.
const nameMap = new Map()

const STORAGE_KEY = 'demoMode'

function persist() {
  if (active.value) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      active: true,
      viewAsTeacher: viewAsTeacher.value,
      scrambleNames: scrambleNames.value,
      previewMissingAttendance: previewMissingAttendance.value,
    }))
  } else {
    sessionStorage.removeItem(STORAGE_KEY)
  }
}

// Call once on app init (main.js), before mount — restores demo mode across
// a page reload within the same tab/session.
function initDemoMode() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null')
    if (saved?.active) {
      active.value = true
      viewAsTeacher.value = saved.viewAsTeacher ?? null
      scrambleNames.value = !!saved.scrambleNames
      previewMissingAttendance.value = !!saved.previewMissingAttendance
    }
  } catch {
    sessionStorage.removeItem(STORAGE_KEY)
  }
}

function enterDemoMode({ teacher = null, scramble = false, missingAttendance = false } = {}) {
  active.value = true
  viewAsTeacher.value = teacher
  scrambleNames.value = scramble
  previewMissingAttendance.value = missingAttendance
  nameMap.clear()
  persist()
}

function exitDemoMode() {
  active.value = false
  viewAsTeacher.value = null
  scrambleNames.value = false
  previewMissingAttendance.value = false
  nameMap.clear()
  persist()
}

// Shared "who should this screen act as" logic — while previewing a teacher,
// every page that locks itself to a teacher name (Dashboard, My Schedule,
// etc.) needs to resolve to the previewed teacher instead of the real
// logged-in user, and through the same scramble cache so it matches whatever
// name the fetch interceptor already baked into the lesson data. `getOwnName`
// supplies the non-demo-mode fallback (the real user's own name).
function previewNameFor(getOwnName) {
  return computed(() => {
    if (!viewAsTeacher.value) return getOwnName()
    return active.value && scrambleNames.value
      ? scrambleName(viewAsTeacher.value.name, nameMap)
      : viewAsTeacher.value.name
  })
}

// Shared "which permissions should this screen enforce" logic — while
// previewing a teacher, any admin-only UI (price fields, scholarship
// checkboxes, admin-only tabs/buttons, etc.) must gate on the previewed
// teacher's own permissions, not the real logged-in admin's, or every
// admin-only control leaks through during a preview. `getOwnPermissions`
// supplies the non-demo-mode fallback (the real user's own permissions).
// permissionsFor is the same rule as a one-off read, for code outside a
// component (the router guard) or that reads localStorage once at setup.
function permissionsFor(ownPermissions) {
  return active.value && viewAsTeacher.value
    ? (viewAsTeacher.value.permissions || [])
    : (ownPermissions || [])
}
function effectivePermissionsFor(getOwnPermissions) {
  return computed(() => permissionsFor(getOwnPermissions()))
}

export function useDemoMode() {
  return {
    active,
    viewAsTeacher,
    scrambleNames,
    previewMissingAttendance,
    previewPickerRequested,
    nameMap,
    enterDemoMode,
    exitDemoMode,
    initDemoMode,
    previewNameFor,
    permissionsFor,
    effectivePermissionsFor,
  }
}
